import { useRef, useState } from 'react';
import { de } from '../../i18n/de';
import { isAcceptedImage, prepareImage } from '../../lib/image';
import { useStoryEnv } from '../../state/storyEnv';
import { toast } from '../../state/toast';

interface Props {
  value: string | null;
  onChange: (assetId: string | null) => void;
  label: string;
}

/**
 * Große Bildfläche: Bild hineinziehen oder klicken; zeigt das Bild, sobald es da ist, und ein Klick darauf wählt ein anderes. Das ✕ oben
 * rechts (erscheint beim Überfahren) entfernt es; dann gilt wieder das Standardbild (core `imageIdOf`).
 */
export function ImageDrop({ value, onChange, label }: Props) {
  const { actions } = useStoryEnv();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);

  const upload = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    if (!isAcceptedImage(file)) return void toast.error(de.details.imageBad);
    setBusy(true);
    try {
      const asset = await actions.uploadAsset(await prepareImage(file));
      if (asset) onChange(asset.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : de.details.imageBad);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <div
      className={`image-drop${over ? ' over' : ''}${value ? ' has-image' : ''}${busy ? ' busy' : ''}`}
      aria-busy={busy || undefined}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        void upload(e.dataTransfer.files?.[0]);
      }}
      data-testid="image-drop"
    >
      <button type="button" className="image-drop-face" onClick={() => input.current?.click()} aria-label={`${label}: ${value ? de.design.imageChange : de.details.imageChoose}`} disabled={busy}>
        {value ? (
          <img src={`/api/assets/${value}`} alt="" draggable={false} />
        ) : (
          <span className="image-drop-hint">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="4" width="18" height="16" rx="2.5" />
              <circle cx="9" cy="10" r="1.8" />
              <path d="M21 16.5l-5-5L6 20" />
            </svg>
            {busy ? de.details.imageUploading : de.design.imageHint}
          </span>
        )}
      </button>
      {value && (
        <button type="button" className="btn small ghost image-drop-remove" onClick={() => onChange(null)} aria-label={`${label}: ${de.details.imageRemove}`} title={de.details.imageRemove}>
          ✕
        </button>
      )}
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" aria-label={`${label}: Datei`} onChange={(e) => void upload(e.target.files?.[0])} data-testid="image-drop-input" />
    </div>
  );
}
